import { FC, useMemo, useState, type JSX } from 'react';
import { OffererInfo } from '../info/OffererInfo';
import { Select } from '../components/Select';
import { OfferType, OptionType } from '../types/SellTradeForm.types';
import countryList from 'react-select-country-list';
import { Checkbox, FormControlLabel, Tooltip } from '@mui/material';
import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
import { useAppSelector } from '../../../hooks/reduxHooks';
import { Button } from '@mui/material';

import { InfoOutlined } from '@mui/icons-material';
import { RobotoFont } from '../../../styles/fonts';

export const customStyles = {
  option: (provided: any, state: any) => ({
    ...provided,
    fontSize: '16px',
    lineHeight: '20px',
    fontFamily: `${RobotoFont.style.fontFamily}, sans-serif`,
    backgroundColor: '#f0f4f8',
    color: '#C8C8C8',
    width: '100%',
    '&:hover': {
      backgroundColor: '#e6f5fe',
    },
  }),
  input: (provided: any) => ({
    ...provided,
    padding: '6.2px 0',
    fontSize: '16px',
    lineHeight: '20px',
    fontWeight: '400',
    fontFamily: `${RobotoFont.style.fontFamily}, sans-serif`,
    color: '#C8C8C8',
  }),
  placeholder: (provided: any) => ({
    ...provided,
    fontSize: '16px',
    lineHeight: '20px',
    fontWeight: 400,
    fontFamily: `${RobotoFont.style.fontFamily}, sans-serif`,
    marginLeft: '12px',
    color: '#C8C8C8',
  }),
  singleValue: (provided: any) => ({
    ...provided,
    fontSize: '16px',
    lineHeight: '20px',
    fontWeight: 400,
    fontFamily: `${RobotoFont.style.fontFamily}, sans-serif`,
    color: '#C8C8C8',
    marginLeft: '12px',
  }),
  control: (provided: any, state: any) => ({
    ...provided,
    height: '42px',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    outline: 'none',
    fontFamily: `${RobotoFont.style.fontFamily}, sans-serif`,
    boxShadow: 'none',
    color: '#C8C8C8',
  }),
  menu: (provided: any, state: any) => ({
    ...provided,
    zIndex: 5,
    backgroundColor: '#f0f4f8',
  }),
  dropdownIndicator: (provided: any) => ({
    paddingRight: 5,
    paddingTop: 4,
  }),
};

export type Props = {
  data: OfferType;
  manageData: (propName: keyof OfferType, payload: string | number | boolean | JSX.Element) => void;
  addData: () => void;
  offererInfoAdded: boolean;
  forModal?: boolean;
  tradeMode: boolean;
  manageTradeMode: (mode: boolean) => void;
  loading: boolean;
  manageModal?: () => void;
};

export const OffererForm: FC<Props> = ({
  data,
  manageData,
  addData,
  offererInfoAdded,
  forModal,
  tradeMode,
  manageTradeMode,
  loading,
  manageModal,
}) => {
  const countryOptions = useMemo(() => countryList().getData(), []);
  const [selectedCountry, setSelectedCountry] = useState<OptionType | undefined>();
  const locale = useAppSelector((st) => st.locale);

  const messages = useMemo(() => {
    return {
      offer_form_step_1: locale['offer_form_step_1'],
      offer_form_name: locale['offer_form_name'],
      offer_form_first_name: locale['offer_form_first_name'],
      offer_form_name_placeholder: locale['offer_form_name_placeholder'],
      offer_form_last_name: locale['offer_form_last_name'],
      offer_form_last_name_placeholder: locale['offer_form_last_name_placeholder'],
      offer_form_email: locale['offer_form_email'],
      offer_form_email_placeholder: locale['offer_form_email_placeholder'],
      offer_form_phone: locale['offer_form_phone'],
      offer_form_phone_placeholder: locale['offer_form_phone_placeholder'],
      offer_form_comments: locale['offer_form_comments'],
      offer_form_comments_placeholder: locale['offer_form_comments_placeholder'],
      offer_form_country: locale['offer_form_country'],
      offer_form_country_placeholder: locale['offer_form_country_placeholder'],
      offer_form_trade_now: locale['offer_form_trade_now'],
      offer_form_button_submit: locale['offer_form_button_submit'],
      offer_form_button_cancel: locale['offer_form_button_cancel'],
      offer_form_button_next: locale['offer_form_button_next'],
    };
  }, [locale]);

  function handleCountrySelect(option: OptionType) {
    setSelectedCountry(option);
    manageData('country', option?.label);
  }
  return (
    <div className='form-container'>
      {offererInfoAdded ? (
        <OffererInfo data={data} />
      ) : (
        <form className='custom-form'>
          <div className='form-row form-flex-row'>
            <div className='input-wrapper'>
              <input
                type='text'
                value={data.firstName}
                onChange={(e) => manageData('firstName', e.target.value)}
                className='custom-input'
                placeholder={`${messages.offer_form_name_placeholder ?? 'First Name'}`}
              />
            </div>
            {!forModal ? (
              <div className='input-wrapper'>
                <input
                  type='text'
                  value={data.lastName}
                  onChange={(e) => manageData('lastName', e.target.value)}
                  className='custom-input'
                  placeholder={`${messages.offer_form_last_name_placeholder ?? 'Last Name'}`}
                />
              </div>
            ) : null}
          </div>
          <div className={`${forModal ? 'form-row' : 'form-row form-flex-row'}`}>
            <div className='input-wrapper'>
              <input
                type='email'
                value={data.email || ''}
                onChange={(e) => manageData('email', e.target.value)}
                className='custom-input'
                placeholder={`${messages.offer_form_email_placeholder ?? 'Email'}`}
              />
            </div>
            <div className='input-wrapper'>
              <div className='group-container' style={{ position: 'relative' }}>
                <Tooltip
                  title={
                    <p style={{ fontSize: '13px', fontWeight: 'normal' }}>{locale.service_page_phone_safe_note}</p>
                  }
                  placement='top-start'
                  enterTouchDelay={0}
                >
                  <InfoOutlined
                    style={{
                      position: 'absolute',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      right: '10px',
                      color: '#C8C8C8',
                    }}
                  />
                </Tooltip>
                <PhoneInput
                  defaultCountry='CZ'
                  placeholder={`${messages.offer_form_phone_placeholder ?? 'Start phone number with +'}`}
                  value={data.phone || ''}
                  onChange={(val: any) => manageData('phone', val)}
                  flags={flags}
                  style={{ padding: '12px', paddingLeft: '12px', paddingRight: '35px', color: 'black' }}
                  className='custom-input'
                  focusInputOnCountrySelection
                  international
                />
              </div>
            </div>
          </div>
          {forModal ? (
            !tradeMode ? (
              <div className='input-wrapper'>
                <textarea
                  className='custom-input text-area'
                  value={data?.comment}
                  onChange={(e) => manageData('comment', e.target.value)}
                  placeholder={`${messages.offer_form_comments_placeholder ?? 'Comments'}`}
                />
              </div>
            ) : (
              ''
            )
          ) : (
            <div className='input-wrapper'>
              <Select
                options={countryOptions}
                value={selectedCountry}
                onChange={handleCountrySelect}
                className='custom-select'
                placeholder={`${messages.offer_form_country_placeholder}`}
                dropdownStyles={customStyles}
              />
            </div>
          )}
          {forModal ? (
            <FormControlLabel
              style={{ maxHeight: '21px' }}
              control={
                <Checkbox
                  className='checkbox-for-modal'
                  checked={tradeMode}
                  onChange={(e) => {
                    manageTradeMode(e.target.checked);
                  }}
                />
              }
              label={<label className='input-label input-label-for-modal'>{`${messages.offer_form_trade_now}`}</label>}
            />
          ) : null}
          {!tradeMode ? (
            <>
              {forModal ? (
                <div style={{ display: 'flex', flexDirection: 'row', gap: '15px' }}>
                  <Button className='button-cancel' variant={'outlined'} onClick={manageModal}>
                    {messages.offer_form_button_cancel ?? 'Cancel'}
                  </Button>
                  <Button className='button-submit' variant={'contained'} onClick={addData}>
                    {messages.offer_form_button_submit ?? 'Submit'}
                  </Button>
                </div>
              ) : (
                <Button className='button-next' variant={'outlined'} onClick={addData}>
                  {messages.offer_form_button_next ?? 'Next'}
                </Button>
              )}
            </>
          ) : null}
        </form>
      )}
    </div>
  );
};
